import { jsx } from 'react/jsx-runtime';
import Shortcut from '@fabric-app/ui/components/Shortcut';
import { IconMobileSheet24KindHighlight, IconDesktop16KindHighlight, IconMobileSheet24TextEditorHeading1, IconTextEditor16Heading1, IconMobileSheet24TextEditorHeading2, IconTextEditor16Heading2, IconMobileSheet24TextEditorHeading3, IconTextEditor16Heading3, IconMobileSheet24TextEditorBulletList, IconTextEditor16BulletList, IconMobileSheet24TextEditorNumberList, IconTextEditor16NumberedList, IconMobileSheet24TextEditorCheckList, IconTextEditor16CheckList, IconMobileSheet24TextEditorToggleList, IconTextEditor16ToggleList, IconMobileSheet24TextEditorCallout, IconTextEditor16Callout, IconMobileSheet24TextEditorQuote, IconTextEditor16Quote, IconMobileSheet24TextEditorCodeblock, IconTextEditor16Codeblock, IconTextEditor20Bold, IconTextEditor20Italic, IconTextEditor20Underline, IconTextEditor20Strikethrough, IconTextEditor20Codeblock, IconTextEditor20Highlight, IconTextEditor20Comment, IconTextEditor20Link, IconTextEditor20Plus, IconTextEditor20Undo, IconTextEditor20Redo, IconTextEditor20BulletList, IconTextEditor20NumberList, IconTextEditor20CheckList, IconTextEditor20ToggleList, IconTextEditor20Indent, IconTextEditor20Unindent } from '@fabric-app/icons/svg';
import { prompt } from '../../../store/alerts.js';
import * as yup from 'yup';
import { Fragment } from '@tiptap/pm/model';
import { getPath, getFirstDescendant, getNodeNameAction, isDirectParentActive } from '../../Extensions/utils.js';
import { ContextualMenuSpecs } from '../../Extensions/ContextualMenu/ContextualMenuConfig.js';
import { ContextualMenuPluginKey } from '../../Extensions/ContextualMenu/ContextualMenuPlugin.js';
import { ContextualMenuMeta } from '../../Extensions/ContextualMenu/ContextualMenuMeta.js';
import { formatUrlWithProtocol } from '@fabric-app/utils/url';
import { URLRegex } from '@fabric-app/utils/text';
import { ToolbarItemType } from '../../../toolbar/toolbar.types.js';
import { aiGroup } from '../../../toolbar/toolbar.config.js';

const linkNodeItem = (editor, selection) => ({
  type: ToolbarItemType.Toggle,
  key: "link",
  label: "Link",
  icon: /* @__PURE__ */ jsx(IconTextEditor20Link, {}),
  getIsActive: () => editor.isActive("link"),
  onClick: () => {
    const isActive = editor.isActive("link");
    if (isActive) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().blur().setTextSelection({
      from: 0,
      to: 0
    }).run();
    setTimeout(async () => {
      editor.chain().focus().command(({ tr }) => {
        tr.setSelection(selection);
        return true;
      }).run();
      const result = await prompt({
        title: "Link",
        confirmLabel: "Insert",
        cancelLabel: "Cancel",
        defaultValue: {
          link: ""
        },
        autoFocus: true,
        placeholders: {
          link: "Add a link"
        },
        validation: yup.object({
          link: yup.string().required().matches(URLRegex, "Invalid URL")
        })
      });
      if (result && result.link) {
        editor?.chain().focus().command(({ tr, chain, editor: editor2 }) => {
          const isEmpty = editor2.state.selection.empty;
          if (!isEmpty) return chain().extendMarkRange("link").setLink({
            href: formatUrlWithProtocol(result.link)
          }).run();
          tr.replaceWith(editor2.state.selection.from, editor2.state.selection.to, Fragment.from(editor2.state.schema.text(result.link, [
            editor2.state.schema.marks.link.create({
              href: formatUrlWithProtocol(result.link)
            })
          ])));
          return true;
        }).run();
      }
    });
  }
});
const modifyNodeItem = (editor) => ({
  type: ToolbarItemType.Selector,
  key: "turnInto",
  label: "Turn into",
  value: () => {
    const { $from, $to } = editor.state.selection;
    if ($from.parent !== $to.parent) return "text";
    const path = getPath($from);
    if (path.length < 2) return "text";
    const parent = path[0];
    const grandParent = path[1];
    if (parent.index === 0 && parent.node.inlineContent && grandParent.node.type.isInGroup("list")) {
      return getNodeNameAction(grandParent.node);
    }
    return getNodeNameAction(parent.node);
  },
  onSelect: (value) => {
    const { $from, $to, from, to } = editor.state.selection;
    if ($from.parent !== $to.parent) return "text";
    const path = getPath($from);
    if (path.length < 2) return "text";
    const parent = path[0];
    const grandParent = path[1];
    let target = parent;
    if (parent.index === 0 && parent.node.inlineContent && (grandParent.node.type.isInGroup("list") || grandParent.node.type.name === "paragraphBlock")) {
      target = grandParent;
    }
    const targetContent = target.node.content;
    let newWrapper = null;
    let nodeType = null;
    let nodeAttrs = null;
    switch (value) {
      case "heading1":
        nodeType = editor.state.schema.nodes.heading;
        nodeAttrs = {
          level: 1
        };
        break;
      case "heading2":
        nodeType = editor.state.schema.nodes.heading;
        nodeAttrs = {
          level: 2
        };
        break;
      case "heading3":
        nodeType = editor.state.schema.nodes.heading;
        nodeAttrs = {
          level: 3
        };
        break;
      case "text":
        nodeType = editor.state.schema.nodes.paragraph;
        break;
      default:
        nodeType = editor.state.schema.nodes[value];
        break;
    }
    if (nodeType.validContent(targetContent)) {
      newWrapper = nodeType.createAndFill(nodeAttrs, targetContent);
    } else if (nodeType.name === "paragraph") {
      newWrapper = editor.state.schema.nodes.paragraphBlock.createAndFill(nodeAttrs, targetContent);
    } else if (nodeType.inlineContent) {
      const firstChild = getFirstDescendant(target);
      const firstChildContent = firstChild.node.content;
      newWrapper = Fragment.from(nodeType.createAndFill(nodeAttrs, firstChildContent)).append(targetContent.cut(0, firstChild.offset - 1).append(targetContent.cut(firstChild.offset + firstChild.node.nodeSize, targetContent.size)));
    } else {
      newWrapper = nodeType.createAndFill(nodeAttrs, editor.state.schema.nodes.paragraph.createAndFill(null, targetContent));
    }
    if (!newWrapper) return;
    editor.chain().focus().command(({ tr }) => {
      tr.replaceWith(target.pos, target.end, newWrapper);
      return true;
    }).setTextSelection({
      from,
      to
    }).run();
  },
  options: [
    {
      icon: /* @__PURE__ */ jsx(IconDesktop16KindHighlight, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24KindHighlight, {}),
      label: "Text",
      value: "text"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16Heading1, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorHeading1, {}),
      label: "Heading 1",
      value: "heading1"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16Heading2, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorHeading2, {}),
      label: "Heading 2",
      value: "heading2"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16Heading3, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorHeading3, {}),
      label: "Heading 3",
      value: "heading3"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16BulletList, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorBulletList, {}),
      label: "Bullet list",
      value: "fbcBulletList"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16NumberedList, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorNumberList, {}),
      label: "Numbered list",
      value: "fbcNumberList"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16CheckList, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorCheckList, {}),
      label: "Checklist",
      value: "fbcCheckList"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16ToggleList, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorToggleList, {}),
      label: "Toggle list",
      value: "fbcToggleList"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16Callout, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorCallout, {}),
      label: "Callout",
      value: "callout"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16Quote, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorQuote, {}),
      label: "Quote",
      value: "blockquote"
    },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16Codeblock, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorCodeblock, {}),
      label: "Code block",
      value: "codeBlock"
    }
  ],
  isHidden: (self) => {
    if (editor.state.selection.$from.start() !== editor.state.selection.$to.start()) return true;
    if (typeof self.value !== "function" || typeof self.value === "undefined") return true;
    const value = self.value();
    if (typeof value !== "string") return true;
    return self.options.every((option) => option.value !== value);
  }
});
const getSelectionText = (selection) => {
  const selectionContent = selection.content();
  return selectionContent.content.textBetween(0, selectionContent.content.size, " ").trim();
};
const aiGroupTiptap = (_editor, selection) => aiGroup(() => getSelectionText(selection));
const basicGroup = (editor, selection) => ({
  name: "basic",
  label: "Basic",
  items: [
    {
      type: ToolbarItemType.Toggle,
      key: "addNode",
      label: "Add node",
      icon: /* @__PURE__ */ jsx(IconTextEditor20Plus, {}),
      getIsActive() {
        const view = editor.view;
        const pluginState = ContextualMenuPluginKey.getState(view.state);
        if (!pluginState) return false;
        return pluginState.active?.name === "addNode";
      },
      onClick: () => {
        const view = editor.view;
        view.dispatch(view.state.tr.setMeta(ContextualMenuPluginKey, new ContextualMenuMeta(ContextualMenuSpecs.addNode, view.state.selection.from, view.state.selection.from, true, true)));
      }
    },
    {
      type: ToolbarItemType.Button,
      key: "undo",
      label: "Undo",
      icon: /* @__PURE__ */ jsx(IconTextEditor20Undo, {}),
      shortcut: (
        // Mod+Z
        /* @__PURE__ */ jsx(Shortcut, {
          modifiers: {
            ctrl: true
          },
          macModifiers: {
            meta: true
          },
          keybind: "Z",
          compact: true
        })
      ),
      onClick: () => editor.chain().focus().undo().run()
    },
    {
      type: ToolbarItemType.Button,
      key: "redo",
      label: "Redo",
      icon: /* @__PURE__ */ jsx(IconTextEditor20Redo, {}),
      shortcut: (
        // Mod+Shift+Z
        /* @__PURE__ */ jsx(Shortcut, {
          modifiers: {
            ctrl: true,
            shift: true
          },
          macModifiers: {
            meta: true,
            shift: true
          },
          keybind: "Z",
          compact: true
        })
      ),
      onClick: () => editor.chain().focus().redo().run()
    },
    modifyNodeItem(editor),
    {
      ...linkNodeItem(editor, selection),
      isHidden: () => {
        const text = getSelectionText(selection);
        return selection.from !== selection.to && !selection.empty && text.length > 0;
      }
    }
  ]
});
const textFormattingGroup = (editor, selection) => ({
  name: "text-formatting",
  label: "Text formatting",
  isHidden: () => {
    const text = getSelectionText(selection);
    return selection.from === selection.to || selection.empty || !text.length;
  },
  items: [
    {
      type: ToolbarItemType.Toggle,
      key: "bold",
      label: "Bold",
      shortcut: (
        // Mod+B
        /* @__PURE__ */ jsx(Shortcut, {
          modifiers: {
            ctrl: true
          },
          macModifiers: {
            meta: true
          },
          keybind: "B",
          compact: true
        })
      ),
      icon: /* @__PURE__ */ jsx(IconTextEditor20Bold, {}),
      getIsActive: () => editor.isActive("bold"),
      onClick: () => editor.chain().focus().toggleBold().run()
    },
    {
      type: ToolbarItemType.Toggle,
      key: "italic",
      label: "Italic",
      shortcut: (
        // Mod+I
        /* @__PURE__ */ jsx(Shortcut, {
          modifiers: {
            ctrl: true
          },
          macModifiers: {
            meta: true
          },
          keybind: "I",
          compact: true
        })
      ),
      icon: /* @__PURE__ */ jsx(IconTextEditor20Italic, {}),
      getIsActive: () => editor.isActive("italic"),
      onClick: () => editor.chain().focus().toggleItalic().run()
    },
    {
      type: ToolbarItemType.Toggle,
      key: "underline",
      label: "Underline",
      shortcut: (
        // Mod+U
        /* @__PURE__ */ jsx(Shortcut, {
          modifiers: {
            ctrl: true
          },
          macModifiers: {
            meta: true
          },
          keybind: "U",
          compact: true
        })
      ),
      icon: /* @__PURE__ */ jsx(IconTextEditor20Underline, {}),
      getIsActive: () => editor.isActive("underline"),
      onClick: () => editor.chain().focus().toggleUnderline().run()
    },
    {
      type: ToolbarItemType.Toggle,
      key: "strike",
      label: "Strike through",
      shortcut: (
        // Mod+Shift+X
        /* @__PURE__ */ jsx(Shortcut, {
          modifiers: {
            ctrl: true,
            shift: true
          },
          macModifiers: {
            meta: true,
            shift: true
          },
          keybind: "X",
          compact: true
        })
      ),
      icon: /* @__PURE__ */ jsx(IconTextEditor20Strikethrough, {}),
      getIsActive: () => editor.isActive("strike"),
      onClick: () => editor.chain().focus().toggleStrike().run()
    },
    linkNodeItem(editor, selection),
    {
      type: ToolbarItemType.Toggle,
      key: "code",
      label: "Inline code",
      shortcut: (
        // Mod+E
        /* @__PURE__ */ jsx(Shortcut, {
          modifiers: {
            ctrl: true
          },
          macModifiers: {
            meta: true
          },
          keybind: "E",
          compact: true
        })
      ),
      icon: /* @__PURE__ */ jsx(IconTextEditor20Codeblock, {}),
      getIsActive: () => editor.isActive("code"),
      onClick: () => editor.chain().focus().toggleCode().run()
    },
    {
      type: ToolbarItemType.Button,
      key: "highlight",
      label: "Highlight",
      shortcut: (
        // Mod+Shift+H
        /* @__PURE__ */ jsx(Shortcut, {
          modifiers: {
            ctrl: true,
            shift: true
          },
          macModifiers: {
            meta: true,
            shift: true
          },
          keybind: "H",
          compact: true
        })
      ),
      icon: /* @__PURE__ */ jsx(IconTextEditor20Highlight, {}),
      onClick: () => editor.chain().focus().createAnnotation(true).run()
    },
    {
      type: ToolbarItemType.Button,
      key: "annotation",
      label: "Annotation",
      icon: /* @__PURE__ */ jsx(IconTextEditor20Comment, {}),
      onClick: () => {
        editor.chain().blur().setTextSelection({
          from: 0,
          to: 0
        }).run();
        setTimeout(() => {
          editor.chain().focus().command(({ tr }) => {
            tr.setSelection(selection);
            return true;
          }).run();
          editor.chain().focus().createAnnotation().run();
        });
      }
    }
  ]
});
const listsGroup = (editor) => ({
  name: "lists",
  label: "Lists",
  items: [
    {
      type: ToolbarItemType.Toggle,
      key: "bulletList",
      label: "Bullet list",
      icon: /* @__PURE__ */ jsx(IconTextEditor20BulletList, {}),
      getIsActive: () => isDirectParentActive(editor, "fbcBulletList"),
      onClick: () => editor.chain().focus().fbcToggleList("fbcBulletList").run()
    },
    {
      type: ToolbarItemType.Toggle,
      key: "numberList",
      label: "Number list",
      icon: /* @__PURE__ */ jsx(IconTextEditor20NumberList, {}),
      getIsActive: () => isDirectParentActive(editor, "fbcNumberList"),
      onClick: () => editor.chain().focus().fbcToggleList("fbcNumberList").run()
    },
    {
      type: ToolbarItemType.Toggle,
      key: "checkList",
      label: "Checklist",
      icon: /* @__PURE__ */ jsx(IconTextEditor20CheckList, {}),
      getIsActive: () => isDirectParentActive(editor, "fbcCheckList"),
      onClick: () => editor.chain().focus().fbcToggleList("fbcCheckList").run()
    },
    {
      type: ToolbarItemType.Toggle,
      key: "toggleList",
      label: "Toggle list",
      icon: /* @__PURE__ */ jsx(IconTextEditor20ToggleList, {}),
      getIsActive: () => isDirectParentActive(editor, "fbcToggleList"),
      onClick: () => editor.chain().focus().fbcToggleList("fbcToggleList").run()
    }
  ]
});
const positioningGroup = (editor) => ({
  name: "positioning",
  label: "Positioning",
  items: [
    {
      type: ToolbarItemType.Button,
      key: "indent",
      label: "Indent",
      icon: /* @__PURE__ */ jsx(IconTextEditor20Indent, {}),
      isDisabled: () => !editor.can().doIndent(),
      onClick: () => {
        editor.chain().focus().doIndent().run();
      }
    },
    {
      type: ToolbarItemType.Button,
      key: "unindent",
      label: "Unindent",
      icon: /* @__PURE__ */ jsx(IconTextEditor20Unindent, {}),
      isDisabled: () => !editor.can().doOutdent(),
      onClick: () => {
        editor.chain().focus().doOutdent().run();
      }
    }
  ]
});

export { aiGroupTiptap, basicGroup, listsGroup, modifyNodeItem, positioningGroup, textFormattingGroup };
//# sourceMappingURL=toolbarConfig.js.map
